import { useEffect, useMemo, useState } from "react";
import { Activity, RefreshCw, ChevronDown } from "lucide-react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { CenterHeader } from "./center/CenterPrimitives";

const C = {
  green: "oklch(0.78 0.18 150)",
  yellow: "oklch(0.85 0.16 90)",
  orange: "oklch(0.78 0.18 55)",
  red: "oklch(0.7 0.21 30)",
  blue: "oklch(0.72 0.16 245)",
  purple: "oklch(0.7 0.18 305)",
  cyan: "oklch(0.78 0.13 200)",
  pink: "oklch(0.78 0.18 0)",
  grid: "oklch(1 0 0 / 0.06)",
  axis: "oklch(0.7 0.02 240)",
};

type Range = "15m" | "1h" | "24h";
const POINTS: Record<Range, number> = { "15m": 30, "1h": 60, "24h": 96 };

const BROKERS = [
  { id: "brokers-0", color: C.green },
  { id: "brokers-1", color: C.yellow },
  { id: "brokers-2", color: C.blue },
  { id: "controllers-3", color: C.orange },
  { id: "controllers-4", color: C.red },
  { id: "controllers-5", color: C.purple },
];

function ts(range: Range, n: number, i: number) {
  const total = range === "15m" ? 15 * 60 : range === "1h" ? 60 * 60 : 24 * 60 * 60;
  const stepSec = total / n;
  const d = new Date(Date.now() - (n - 1 - i) * stepSec * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function series(range: Range, gen: (i: number) => Record<string, number>) {
  const n = POINTS[range];
  return Array.from({ length: n }, (_, i) => ({ t: ts(range, n, i), ...gen(i) }));
}

function Tip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-black/85 px-2.5 py-1.5 text-[11px] shadow-xl backdrop-blur">
      <div className="text-muted-foreground">{label}</div>
      {payload.slice(0, 8).map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span>{p.name}</span>
          <span className="ml-auto font-mono tabular-nums">{Number(p.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function Card({
  title,
  children,
  legend,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  legend?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={"card-surface rounded-2xl p-4 " + className}>
      <div className="mb-2 text-xs font-medium text-muted-foreground">{title}</div>
      {children}
      {legend && <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">{legend}</div>}
    </div>
  );
}

function Kpi({ label, value, tone = "green" }: { label: string; value: number | string; tone?: "green" | "red" }) {
  const color = tone === "red" ? C.red : C.green;
  return (
    <div className="card-surface rounded-xl p-3">
      <div className="text-[11px] text-muted-foreground truncate">{label}</div>
      <div className="mt-1 text-4xl font-light" style={{ color }}>{value}</div>
    </div>
  );
}

function BrokerLegend({ ids = BROKERS }: { ids?: typeof BROKERS }) {
  return (
    <>
      {ids.map((b) => (
        <span key={b.id} className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-3 rounded-sm" style={{ background: b.color }} />
          {b.id}
        </span>
      ))}
    </>
  );
}

export default function KafkaMetricsView({ onBack }: { onBack: () => void }) {
  const [range, setRange] = useState<Range>("1h");
  const [auto, setAuto] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [auto]);

  const perBroker = (base: number, amp: number, seed = 1) =>
    series(range, (i) => {
      const o: Record<string, number> = {};
      BROKERS.forEach((b, k) => {
        o[b.id] = base + Math.sin(i / (3 + k) + seed + k) * amp + Math.cos(i / (5 + k) + tick * 0.1) * amp * 0.4;
      });
      return o;
    });

  const memory = useMemo(() => perBroker(2.4, 0.6, 1), [range, tick]);
  const cpu = useMemo(() => perBroker(0.25, 0.08, 2), [range, tick]);
  const disk = useMemo(() => perBroker(28, 2, 3), [range]);
  const fds = useMemo(() => perBroker(750, 50, 4), [range, tick]);

  const byteRate = useMemo(
    () => series(range, (i) => ({ in: 60 + Math.sin(i / 3 + tick * 0.1) * 30, out: 90 + Math.cos(i / 4 + tick * 0.1) * 25 })),
    [range, tick],
  );
  const msgs = useMemo(
    () => series(range, (i) => ({ rate: 3 + Math.sin(i / 1.2 + tick * 0.3) * 1.2 + (Math.random() - 0.5) * 0.5 })),
    [range, tick],
  );
  const fetchReq = useMemo(
    () => series(range, (i) => ({ rate: 480 + Math.sin(i / 2 + tick * 0.2) * 80 })),
    [range, tick],
  );

  const idleNet = useMemo(() => perBroker(99.5, 0.4, 5), [range, tick]);
  const idleReq = useMemo(() => perBroker(99.98, 0.02, 6), [range, tick]);
  const diskWrites = useMemo(() => perBroker(40, 25, 7), [range, tick]);
  const diskReads = useMemo(() => perBroker(60, 30, 8), [range, tick]);

  return (
    <div className="flex flex-col gap-4">
      <CenterHeader title="Strimzi Kafka Dashboard with JVM Metrics" scope="Brokers · Topics · Partitions · Throughput — live" onBack={onBack} />

      {/* Toolbar */}
      <div className="card-surface flex flex-wrap items-center gap-2 rounded-2xl p-2">
        {[
          ["datasource", "Prometheus"],
          ["Namespace", "kafka"],
          ["Cluster Name", "All"],
          ["Broker", "All"],
          ["Topic", "All"],
          ["Partition", "All"],
        ].map(([k, v]) => (
          <div key={k} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs">
            <span className="text-muted-foreground">{k}</span>
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px]">{v}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </div>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {(["15m", "1h", "24h"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={
                "rounded-full px-3 py-1.5 text-xs " +
                (range === r ? "bg-foreground text-background" : "bg-white/5 text-muted-foreground hover:text-foreground")
              }
            >
              Last {r}
            </button>
          ))}
          <button
            onClick={() => setAuto((a) => !a)}
            className={
              "ml-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs " +
              (auto ? "bg-success/15 text-success" : "bg-white/5 text-muted-foreground")
            }
          >
            <Activity className="h-3 w-3" /> {auto ? "Live 5s" : "Paused"}
          </button>
          <button
            onClick={() => setTick((t) => t + 1)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        <Kpi label="Brokers Online" value={3} />
        <Kpi label="Active Controllers" value={1} />
        <Kpi label="Unclean Leaders" value={0} />
        <Kpi label="Online Replicas" value={168} />
        <Kpi label="Under Replicated" value={0} />
        <Kpi label="Partitions at min ISR" value={0} />
        <Kpi label="Partitions under min ISR" value={0} />
        <Kpi label="Offline Partitions" value={0} />
      </div>

      {/* Kafka section: resources */}
      <div className="text-xs font-medium text-muted-foreground">Kafka</div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <Card title="Memory Usage" legend={<BrokerLegend />}>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={memory} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} tickFormatter={(v) => `${v.toFixed(1)} GiB`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Area key={b.id} type="monotone" dataKey={b.id} stroke={b.color} fill={b.color} fillOpacity={0.15} strokeWidth={1.2} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="CPU Usage" legend={<BrokerLegend />}>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cpu} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={36} tickFormatter={(v) => v.toFixed(2)} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Line key={b.id} type="monotone" dataKey={b.id} stroke={b.color} strokeWidth={1.2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Available Disk Space" legend={<BrokerLegend />}>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={disk} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} tickFormatter={(v) => `${Math.round(v)} GiB`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Area key={b.id} type="monotone" dataKey={b.id} stroke={b.color} fill={b.color} fillOpacity={0.18} strokeWidth={1.2} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Open File Descriptors" legend={<BrokerLegend />}>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fds} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={36} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Line key={b.id} type="monotone" dataKey={b.id} stroke={b.color} strokeWidth={1.2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Throughput row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Total Incoming Byte Rate" value="108 kB/s" />
        <Kpi label="Total Outgoing Byte Rate" value="118 MB/s" />
        <Kpi label="Incoming Messages Rate" value="3.17 wr/s" />
        <Kpi label="Fetch Request Rate" value="2.97 req/s" />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card title="Byte Rate" legend={<><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-3 rounded-sm" style={{ background: C.green }}/>in</span><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-3 rounded-sm" style={{ background: C.yellow }}/>out</span></>}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={byteRate} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={48} tickFormatter={(v) => `${Math.round(v)} MiB`} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="in" stroke={C.green} fill={C.green} fillOpacity={0.25} strokeWidth={1.4} />
                <Area type="monotone" dataKey="out" stroke={C.yellow} fill={C.yellow} fillOpacity={0.2} strokeWidth={1.4} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Messages In Per Second" legend={<span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-3 rounded-sm" style={{ background: C.green }}/>Total Incoming Messages Rate</span>}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={msgs} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={32} domain={[0, 6]} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="rate" stroke={C.green} strokeWidth={1.2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="Fetch Request Rate">
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={fetchReq} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} />
              <Tooltip content={<Tip />} />
              <Area type="monotone" dataKey="rate" stroke={C.cyan} fill={C.cyan} fillOpacity={0.25} strokeWidth={1.4} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Idle percents */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card title="Network Processor Avg Idle Percent" legend={<BrokerLegend />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={idleNet} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={36} domain={[97.5, 100]} tickFormatter={(v) => `${v.toFixed(0)}%`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Area key={b.id} type="monotone" dataKey={b.id} stroke={b.color} fill={b.color} fillOpacity={0.18} strokeWidth={1} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Request Handler Avg Idle Percent" legend={<BrokerLegend />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={idleReq} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} domain={[99.95, 100]} tickFormatter={(v) => `${v.toFixed(2)}%`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Area key={b.id} type="monotone" dataKey={b.id} stroke={b.color} fill={b.color} fillOpacity={0.18} strokeWidth={1} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Disk I/O */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card title="Disk Writes" legend={<BrokerLegend />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={diskWrites} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={48} tickFormatter={(v) => `${Math.round(v)} MiB`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Line key={b.id} type="monotone" dataKey={b.id} stroke={b.color} strokeWidth={1.1} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Disk Reads" legend={<BrokerLegend />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={diskReads} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={48} tickFormatter={(v) => `${Math.round(v)} MiB`} />
                <Tooltip content={<Tip />} />
                {BROKERS.map((b) => (
                  <Line key={b.id} type="monotone" dataKey={b.id} stroke={b.color} strokeWidth={1.1} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
